import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { AutofillSourceService } from './autofill-source.service';
import {
  IConstructorAccessResponse,
  IConstructorDraft,
  IConstructorDraftPayload,
  IConstructorDraftSaveResponse,
  IConstructorDraftSummary,
  IConstructorFoundersResponse,
  IConstructorLegacyPendingResponse,
  IConstructorPendingDocument,
  IConstructorPendingResponse,
  IConstructorPreviewRequest,
  IConstructorPreviewResponse,
  IConstructorSet,
  IConstructorSetContext,
  IConstructorSetDocument,
  IConstructorSignRequest,
  IConstructorSignResponse,
  IConstructorSignedFileResponse
} from '../types/constructor.types';

@Injectable({
  providedIn: 'root'
})
export class ConstructorService {
  private apiUrl = environment.api_url + environment.api_version + '/constructor';

  constructor(private http: HttpClient, private authService: AuthService, private autofillSource: AutofillSourceService) {
  }

  private iinHeaders(): HttpHeaders {
    return new HttpHeaders();
    // return new HttpHeaders({ iin: this.authService.user?.iin || '' });
  }

  getSet(foundersCount: number, opf: string): Observable<IConstructorSet> {
    return this.http.get<IConstructorSet>(`${this.apiUrl}/set`, {
      headers: this.iinHeaders(),
      params: { founders: String(foundersCount), opf }
    });
  }

  getSetContext(bin: string): Observable<IConstructorSetContext> {
    return this.http.get<IConstructorSetContext>(`${this.apiUrl}/set-context`, {
      headers: this.iinHeaders(),
      params: { bin }
    });
  }

  private static readonly FOUNDERS_URL = '/constructor/founders';

  getFounders(): Observable<IConstructorFoundersResponse> {
    return this.autofillSource.get<IConstructorFoundersResponse>(ConstructorService.FOUNDERS_URL);
  }

  clearFoundersCache(): void {
    this.autofillSource.invalidate(ConstructorService.FOUNDERS_URL);
  }

  getAccess(): Observable<boolean> {
    return this.http.get<IConstructorAccessResponse>(`${this.apiUrl}/access`, {
      headers: this.iinHeaders(),
    }).pipe(map(res => !!res?.allowed));
  }

  getPending(bin?: string): Observable<IConstructorPendingDocument[]> {
    const cleanBin = bin && !['null', 'undefined', 'none'].includes(bin.trim().toLowerCase()) ? bin.trim() : '';
    return this.http.get<IConstructorPendingResponse | IConstructorLegacyPendingResponse | IConstructorSetDocument[]>(`${this.apiUrl}/pending`, {
      headers: this.iinHeaders(),
      params: cleanBin ? { bin: cleanBin } : {}
    }).pipe(map(response => {
      if (Array.isArray(response)) {
        return response.map(document => ({ ...document, bin: cleanBin, owner: '' }));
      }
      if ('companies' in response && Array.isArray(response.companies)) {
        return response.companies.reduce<IConstructorPendingDocument[]>((all, company) => [
          ...all,
          ...(company.documents || []).map(document => ({ ...document, bin: company.bin, owner: company.owner }))
        ], []);
      }
      if ('documents' in response) {
        return (response.documents || []).map(document => ({
          ...document,
          bin: response.bin,
          owner: response.owner
        }));
      }
      return [];
    }));
  }

  getDrafts(bin: string): Observable<IConstructorDraftSummary[]> {
    return this.http.get<IConstructorDraftSummary[]>(`${this.apiUrl}/drafts`, {
      headers: this.iinHeaders(),
      params: { bin }
    });
  }

  getDraft(docType: string, bin: string): Observable<IConstructorDraft> {
    return this.http.get<IConstructorDraft>(`${this.apiUrl}/draft/${docType}`, {
      headers: this.iinHeaders(),
      // params: { bin }
    });
  }

  // Чужой черновик (пользователь — со-подписант, а не инициатор): бэк не
  // резолвит его по iin текущего пользователя + docType + bin (см. getDraft
  // выше — тот путь адресован владельцу), а отдаёт по конкретному draftId,
  // который приходит в каждом документе из GET /constructor/pending (см.
  // "pending" flow в pending-documents.component.ts/onSignCardClick).
  getDraftById(draftId: string): Observable<IConstructorDraft> {
    return this.http.get<IConstructorDraft>(`${this.apiUrl}/draft/${draftId}`, {
      headers: this.iinHeaders()
    });
  }

  saveDraft(docType: string, payload: IConstructorDraftPayload): Observable<IConstructorDraftSaveResponse> {
    return this.http.put<IConstructorDraftSaveResponse>(`${this.apiUrl}/draft/${docType}`, payload, { headers: this.iinHeaders() });
  }

  createDraft(payload: IConstructorDraftPayload): Observable<IConstructorDraftSaveResponse> {
    return this.http.post<IConstructorDraftSaveResponse>(`${this.apiUrl}/draft`, payload, { headers: this.iinHeaders() });
  }

  deleteDraft(docType: string, bin: string): Observable<{ id: string }> {
    return this.http.delete<{ id: string }>(`${this.apiUrl}/draft/${docType}`, {
      headers: this.iinHeaders(),
      params: { bin }
    });
  }

  deleteSigned(documentId: string): Observable<unknown> {
    return this.http.delete(`${this.apiUrl}/signed/${documentId}`, { headers: this.iinHeaders() });
  }

  // POST /constructor/reject?draftId= — со-подписант отклоняет документ,
  // причина — в теле ({reason}).
  reject(draftId: string, reason: string): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/reject`, { reason }, {
      headers: this.iinHeaders(),
      params: { draftId }
    });
  }

  // GET /constructor/signed/{draftId}/file — PDF подписанного документа,
  // собранный на бэке. lang: одна языковая версия или обе (ru|kk|both).
  // Бэк отдаёт JSON, сам файл лежит в content в base64 — декодируем в Blob.
  getSignedFile(draftId: string, lang: string): Observable<Blob> {
    return this.http.get<IConstructorSignedFileResponse>(`${this.apiUrl}/signed/${draftId}/file`, {
      headers: this.iinHeaders(),
      params: { lang }
    }).pipe(
      map(response => {
        const binary = window.atob(response.content);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        return new Blob([bytes], { type: response.contentType || 'application/pdf' });
      })
    );
  }

  preview(payload: IConstructorPreviewRequest): Observable<IConstructorPreviewResponse> {
    return this.http.post<IConstructorPreviewResponse>(`${this.apiUrl}/preview`, payload);
  }

  // Бэк требует docType вместе с draftId ("Missing param name: docType").
  getForSignature(draftId: string, docType: string): Observable<string> {
    return this.http.get(`${this.apiUrl}/for_signature`, {
      headers: this.iinHeaders(),
      params: { draftId, docType },
      responseType: 'text'
    });
  }

  // POST /constructor/sign?draftId= — черновик адресуется по draftId в query,
  // docType + bin в теле остаются запасным путём для физлиц.
  sign(payload: IConstructorSignRequest): Observable<IConstructorSignResponse> {
    const { draftId, ...body } = payload;
    return this.http.post<IConstructorSignResponse>(`${this.apiUrl}/sign`, body, {
      headers: this.iinHeaders(),
      params: draftId ? { draftId } : {}
    });
  }
}
